/**
 * FERP Portal - Metric List Page
 *
 * OPTIMIZE phase primitive - Analytics/Metrics.
 * Displays analytics records from backend via useListAnalyticsQuery.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Data from backend via useListAnalyticsQuery
 * - Multi-tenant via useTenantId()
 * - Dumb Frontend: All data from backend, no hardcoded values
 */

import { useMemo, useState } from 'react';
import { useTerminology } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  TextInput,
} from '@mantine/core';
import {
  IconChartBar,
  IconAlertTriangle,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListAnalyticsQuery,
  type AnalyticsFieldsFragment,
} from '@/generated/graphql';
import { getPrimitiveMetadata } from '@/config/primitive-metadata';

const analyticsMeta = getPrimitiveMetadata('analytics');

export function MetricListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const [searchQuery, setSearchQuery] = useState('');

  const { data, loading, error, refetch } = useListAnalyticsQuery({
    variables: { tenantId, limit: 100 },
    skip,
  });

  const analytics = useMemo(() => {
    return data?.listAnalytics?.nodes ?? [];
  }, [data]);

  const filteredAnalytics = useMemo(() => {
    if (!searchQuery) return analytics;
    const query = searchQuery.toLowerCase();
    return analytics.filter((a: AnalyticsFieldsFragment) =>
      a.analyticsName.toLowerCase().includes(query) ||
      a.analyticsCode.toLowerCase().includes(query) ||
      a.metricType?.toLowerCase().includes(query) ||
      a.metricCategory?.toLowerCase().includes(query)
    );
  }, [analytics, searchQuery]);

  const stats = useMemo(() => {
    const total = analytics.length;
    const enabled = analytics.filter((a: AnalyticsFieldsFragment) => a.enabled).length;
    const withWarnings = analytics.filter((a: AnalyticsFieldsFragment) =>
      a.warningThreshold != null && a.currentValue >= a.warningThreshold
    ).length;
    return { total, enabled, withWarnings };
  }, [analytics]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view metrics.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Analytics">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>{translate('metric')}s</Title>
            <Text c="dimmed" size="sm">
              System health and {translate('workflow').toLowerCase()} performance {translate('analytics').toLowerCase()}
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats Cards */}
        <Group gap="md">
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm">
                <IconChartBar size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total</Text>
                <Text size="xl" fw={700}>{stats.total}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="green">
                <IconChartBar size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Enabled</Text>
                <Text size="xl" fw={700}>{stats.enabled}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="yellow">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Warnings</Text>
                <Text size="xl" fw={700}>{stats.withWarnings}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Search metrics..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
        />

        {/* Analytics Table */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Metric</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Current</Table.Th>
                  <Table.Th>Previous</Table.Th>
                  <Table.Th>Unit</Table.Th>
                  <Table.Th>Enabled</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredAnalytics.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="md">
                        No analytics found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredAnalytics.slice(0, 50).map((metric: AnalyticsFieldsFragment) => (
                    <Table.Tr key={metric.analyticsId}>
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="sm" fw={500} lineClamp={1}>{metric.analyticsName}</Text>
                          <Text size="xs" c="dimmed">{metric.analyticsCode}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm">{metric.metricType}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline" size="sm">{metric.metricCategory}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          fw={500}
                          c={
                            metric.criticalThreshold != null && metric.currentValue >= metric.criticalThreshold
                              ? 'red'
                              : metric.warningThreshold != null && metric.currentValue >= metric.warningThreshold
                              ? 'yellow'
                              : undefined
                          }
                        >
                          {metric.currentValue}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{metric.previousValue}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{metric.metricUnit}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={metric.enabled ? 'green' : 'gray'}
                          size="sm"
                          variant={metric.enabled ? 'filled' : 'light'}
                        >
                          {metric.enabled ? 'Yes' : 'No'}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        <Badge variant="light" color={analyticsMeta.phaseColor} style={{ alignSelf: 'flex-start' }}>
          {analyticsMeta.phase} Phase
        </Badge>
      </Stack>
    </Container>
  );
}

export default MetricListPage;
